<script setup lang="ts">
import { useThrottleFn, useEventListener } from '@vueuse/core'

const { t, locale } = useI18n()
const { search } = useConstructor()
const { trackSearchResultsLoaded } = useConstructorTracker()
const route = useRoute()
const { getFacetsFromURL, getFormattedFilters, getGrouped } = useSiteUrl()
const { add: addRecentSearch } = useRecentSearches()
const preferencesStore = usePreferencesStore()
const topCategories = preferencesStore?.site?.topCategories
const SEARCH_RESULTS_LIST = 'search results'
const loading = ref(true)
const products = ref<Omit<ProductTileProps, 'index'>[]>([])
const totalProducts = ref(0)
const facets = ref()
const query = ref()
const newSearchString = ref('')
const sortOptions = ref<SortOpion[]>([])
const relatedSearchTerms = ref<string[]>([])
const relatedSearchesRef = ref<HTMLElement | null>(null)
const scrollOffsetLeft = ref(0)
const scrollOffsetRight = ref(0)

provide('gaOptions', {
  group: 'search results',
})

const getConstructorRequestData = () => {
  const facetsFromUrl = getFacetsFromURL()
  const { sortByValue, sortOrderValue, q } = facetsFromUrl
  let { filters } = facetsFromUrl
  filters = filters ? getFormattedFilters(filters) : {}
  let price = filters?.price
  if (price?.[0]) {
    price = [price[0].replace(/[()]/g, '').replace('..', '-')]
  }

  return { filters, price, sortByValue, sortOrderValue, q }
}

const handleRedirect = (url: string) => {
  const allowedLocaleUrlSegments = ['fr', 'en']
  const urlParts = route.path.split('/')
  let localeUrlSegment = ''
  if (urlParts[1]) {
    localeUrlSegment = allowedLocaleUrlSegments.includes(urlParts[1]) ? `/${urlParts[1]}` : ''
  }
  window.location.href = localeUrlSegment + url
}

const loadNextPage = async () => {
  loading.value = true
  const positionOffset = products.value.length
  const { filters, price, sortByValue, sortOrderValue, q } = getConstructorRequestData()
  const term = q?.toString() || ''
  const sorting = {
    sortBy: sortByValue,
    sortOrder: sortOrderValue,
  }
  const newResponse = await search(term, {
    filters: {
      ...filters,
      price,
    },
    ...sorting,
    resultsPerPage: 24,
    offset: positionOffset,
  })
  products.value = [...products.value, ...reactive(newResponse?.productsResult || [])]
  trackSearchResultsLoaded(term, {
    url: window.location.href,
    items: mapConstructorItemIdFromSearch(newResponse?.productsResult || []),
    resultCount: newResponse?.productsResult?.length || 0,
    resultPage: Math.floor(products.value.length / 24) + 1,
    selectedFilters: filters,
    ...sorting,
  })
  trackProductListRender({
    list: SEARCH_RESULTS_LIST,
    location: 'search page',
    group: 'product list',
    label: term,
    products: (newResponse?.productsResult || []).map((product: any) => product.productData),
    positionOffset,
    locale: locale.value,
  })
  loading.value = false
}

const handleSearch = async () => {
  if (newSearchString.value) {
    const newSearchResponse = await search(newSearchString.value, {})
    addRecentSearch(newSearchString.value)
    // useCookie writes on the next tick; the page we leave for reads the history from the request's cookie.
    await nextTick()
    const redirectURL = newSearchResponse?.data?.url
    if (redirectURL) {
      handleRedirect(redirectURL)
    } else {
      const searchTerm = encodeURI(newSearchString.value.trimStart().trimEnd().replaceAll("'", ''))
      handleRedirect(`/search?q=${searchTerm}`)
    }
  }
}

const initPage = async () => {
  loading.value = true
  const { filters, price, sortByValue, sortOrderValue, q } = getConstructorRequestData()
  query.value = q
  const term = q?.toString() || ''
  const sorting = {
    sortBy: sortByValue,
    sortOrder: sortOrderValue,
  }
  const searchResponse = await search(term, {
    filters: {
      ...filters,
      price,
    },
    ...sorting,
    sortOrder: sortOrderValue,
    resultsPerPage: 24,
  })

  const { productsResult, sortOptionsResults, facetsResult, relatedSearches } = searchResponse || {}
  const redirectURL = searchResponse?.data?.url
  if (redirectURL) handleRedirect(redirectURL)
  products.value = productsResult?.length > 0 ? productsResult : undefined
  totalProducts.value = facetsResult?.data?.total
  facets.value = facetsResult ? getGrouped(facetsResult) : undefined
  sortOptions.value = sortOptionsResults || []
  relatedSearchTerms.value = relatedSearches || []
  trackSearchResultsLoaded(term, {
    url: window.location.href,
    items: mapConstructorItemIdFromSearch(productsResult || []),
    resultCount: productsResult?.length || 0,
    resultPage: 1,
    selectedFilters: filters,
    ...sorting,
  })
  trackProductListRender({
    list: SEARCH_RESULTS_LIST,
    location: 'search page',
    group: 'product list',
    label: term,
    products: (productsResult || []).map((product: any) => product.productData),
    locale: locale.value,
  })
  loading.value = false
}

const setScrollPosition = useThrottleFn(() => {
  if (!relatedSearchesRef.value) return

  const element = relatedSearchesRef.value
  const scrollLeft = element.scrollLeft
  const scrollWidth = element.scrollWidth
  const clientWidth = element.clientWidth

  if (scrollWidth <= clientWidth) {
    scrollOffsetLeft.value = 0
    scrollOffsetRight.value = 0
    return
  }

  scrollOffsetLeft.value = scrollLeft > 0 ? 1 : 0

  scrollOffsetRight.value = scrollLeft < scrollWidth - clientWidth ? 1 : 0
}, 100)

onBeforeMount(async () => {
  initPage()
})

usePageContentType().setContentType('search results')

onMounted(async () => {
  products.value = products.value?.length > 0 ? products.value : []
  onMountedFirstLoadHandler()
})

watch(() => route.query, initPage)

watch(
  relatedSearchTerms,
  async (newTerms) => {
    if (newTerms && newTerms.length > 0) {
      await nextTick()
      if (relatedSearchesRef.value) {
        useEventListener(relatedSearchesRef.value, 'scroll', setScrollPosition)
        useEventListener(window, 'resize', setScrollPosition)
        setScrollPosition()
      }
    }
  },
  { immediate: true },
)

useSeoHead({
  title: t('search_page.seo_title', { delimiter: '|' }),
  description: t('search_page.seo_title'),
  locale: locale.value,
  path: route.path,
})
</script>

<template>
  <div class="search">
    <h1 v-if="query && !loading" data-testid="search-heading">
      {{
        t(products ? 'search_page.search_results_for_product' : 'search_page.no_search_results_title', { query: query })
      }}
    </h1>

    <div v-if="relatedSearchTerms" class="search__related-searches-wrapper">
      <div ref="relatedSearchesRef" class="search__related-searches" @scroll="setScrollPosition">
        <!-- NOTE: NuxtLink works better here but Anchor is used to trigger dataLayer events -->
        <Anchor v-for="(search, index) in relatedSearchTerms" :key="index" :url="`/search?q=${search}`">
          <svg-icon name="mhj:search" />
          {{ search }}</Anchor
        >
      </div>
      <div v-if="scrollOffsetLeft > 0" class="search__left-overlay" />
      <div v-if="scrollOffsetRight > 0" class="search__right-overlay" />
    </div>

    <ProductList
      v-if="products"
      :products="products"
      :facets="facets"
      :total-products="totalProducts"
      :sort-options="sortOptions"
      :list-name="SEARCH_RESULTS_LIST"
      :loading="loading"
      @next-page="loadNextPage"
    />
    <div v-else class="search__no-results" data-testid="search-no-results">
      <div class="search__new-search-section">
        <p>{{ t('search_page.no_products_text_for_search') }} {{ t('search_page.try_new_search') }}</p>
        <div class="search__new-search">
          <input
            v-model="newSearchString"
            name="newSearch"
            data-testid="search-no-results-input"
            class="search__input"
            :placeholder="t('search_page.input_search_placeholder')"
            @keyup.enter="handleSearch"
          />
          <Button variant="primary" @click="handleSearch">
            {{ t('search_page.button_go_text') }}
          </Button>
        </div>
      </div>

      <CardPanelSwitcher v-if="topCategories" v-bind="topCategories" />
    </div>

    <BrazeBanner
      v-if="preferencesStore.site?.searchBrazeBanner?.placementIds?.length"
      :placement-ids="preferencesStore.site.searchBrazeBanner.placementIds"
      :supporting-data="preferencesStore.site.searchBrazeBanner.supportingData"
    />
  </div>
</template>

<style lang="postcss">
.search {
  margin: var(--space-00) auto;
  max-width: var(--container-max-width);
  padding-inline: var(--space-50);

  h1 {
    padding-block: 2rem;
    margin: 0;
  }

  .search__related-searches-wrapper {
    position: relative;
    margin-block: var(--space-70);

    .search__related-searches {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-40);

      @media (--mobile-only) {
        flex-wrap: nowrap;
        overflow-x: overlay;
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
          display: none;
        }
      }

      a {
        display: inline-flex;
        flex-shrink: 0;
        gap: var(--space-20);
        align-items: center;
        text-decoration: none;
        padding: var(--space-10) var(--space-20);
        border: 1px solid var(--border-input);
        border-radius: var(--border-radius-4);

        &:hover {
          border-color: var(--border-dark-30);
        }
      }
    }

    .search__left-overlay,
    .search__right-overlay {
      width: var(--space-80);
      height: 100%;
      position: absolute;
      top: 0;

      @media (--tablet) {
        width: var(--space-60);
      }

      @media not (--mobile-only) {
        display: none;
      }
    }

    .search__left-overlay {
      left: 0;
      background: linear-gradient(270deg, rgba(255, 255, 255, 0), #ffffff);
    }

    .search__right-overlay {
      right: 0;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0), #ffffff);
    }
  }

  .search__new-search-section {
    max-width: 32rem;
    margin-inline: auto;
    text-align: center;
    padding: var(--space-120) var(--space-50);

    .search__new-search {
      display: flex;
      .search__input {
        flex: 1;
        border: 1px solid var(--border-input);
        border-right: 0;
        padding-left: var(--space-50);

        @media (--mobile-tablet-only) {
          font-size: var(--space-50); /* Avoid iOS zoom min font size */
        }
      }
      button {
        min-width: unset;
      }
    }
  }
}
</style>
